import type {
  EquityOrder,
  EquityOrderUpdateResult,
} from "@/generated/TradingGateway"
import {
  Direction,
  OrderStatus,
  OrderType,
  SUCCESS_EQUITY_ORDER_UPDATE_RESULT,
  TimeInForce,
  UpdateType,
} from "@/generated/TradingGateway"

import { mockInstruments } from "./equityInstrumentServiceData"

const orderHistoryMockData = [
  {
    id: "c32be196-fbfb-4c47-ad8f-18f4ec5e0281",
    clientOrderId: "ARU-25c23657-5920-4042-9110-cae9e9f259b2",
    createdAt: "2023-12-01T09:33:03.240841Z",
    updatedAt: "2023-12-01T09:33:03.240841Z",
    submittedAt: "2023-12-01T09:33:03.239802Z",
    filled_at: null,
    asset_id: "8ccae427-5dd0-45b3-b5fe-7ba5e422c766",
    symbol: "TSLA",
    asset_class: "us_equity",
    notional: null,
    qty: "92.673",
    filledQty: "0",
    filled_avg_price: null,
    order_class: "",
    orderType: "market",
    type: "order",
    side: "buy",
    time_in_force: "day",
    limit_price: null,
    stop_price: null,
    status: "accepted",
    extended_hours: false,
    commission: "0",
    source: "correspondent",
    instrument: mockInstruments.find((inst) => inst.symbol === "TSLA"),
  },
  {
    id: "3b55ee9b-daa0-42b8-a1fa-fa604f20e4d2",
    clientOrderId: "ARU-a47888a4-265a-4b2a-89a1-df513050b35e",
    createdAt: "2023-12-01T09:32:21.452377Z",
    updatedAt: "2023-12-01T09:32:21.452377Z",
    submittedAt: "2023-12-01T09:32:21.451226Z",
    filled_at: null,
    asset_id: "8ccae427-5dd0-45b3-b5fe-7ba5e422c766",
    symbol: "TSLA",
    asset_class: "us_equity",
    notional: null,
    qty: "11.12",
    filledQty: "0",
    filled_avg_price: null,
    order_class: "",
    orderType: "market",
    type: "order",
    side: "buy",
    time_in_force: "day",
    limit_price: null,
    stop_price: null,
    status: "accepted",
    extended_hours: false,
    commission: "0",
    source: "correspondent",
    instrument: mockInstruments.find((inst) => inst.symbol === "TSLA"),
  },
  {
    id: "91a0ccaf-3bcd-4c97-94d8-d2ad77656a2e3",
    clientOrderId: "ARU-44edfe05-3c54-4f64-8319-aa3e937ef231",
    createdAt: "2023-12-01T09:31:42.75232Z",
    updatedAt: "2023-12-01T09:31:42.75232Z",
    submittedAt: "2023-12-01T09:31:42.751734Z",
    filled_at: null,
    asset_id: "4ce9353c-66d1-46c2-898f-fce867ab0247",
    symbol: "NVDA",
    asset_class: "us_equity",
    notional: null,
    qty: "3",
    filledQty: "0",
    filled_avg_price: null,
    order_class: "",
    orderType: "market",
    type: "order",
    side: "buy",
    time_in_force: "day",
    limit_price: null,
    stop_price: null,
    status: "accepted",
    extended_hours: false,
    commission: "0",
    source: "correspondent",
    instrument: mockInstruments.find((inst) => inst.symbol === "NVDA"),
  },
  {
    id: "9806c301-5a87-4b3a-b56e-6ea0d0ab03a44",
    clientOrderId: "ARU-a071b678-45e9-45b6-9260-305a7ee85224",
    createdAt: "2023-11-30T13:51:45.925686Z",
    updatedAt: "2023-11-30T13:51:45.925686Z",
    submittedAt: "2023-11-30T13:51:45.92503Z",
    filled_at: null,
    asset_id: "b6d1aa75-5c9c-4353-a305-9e2caa1925ab",
    symbol: "MSFT",
    asset_class: "us_equity",
    notional: null,
    qty: "163",
    filledQty: "0",
    filled_avg_price: null,
    order_class: "",
    orderType: "market",
    type: "order",
    side: "buy",
    time_in_force: "day",
    limit_price: null,
    stop_price: null,
    status: "accepted",
    extended_hours: false,
    commission: "0",
    source: "correspondent",
    instrument: mockInstruments.find((inst) => inst.symbol === "MSFT"),
  },
]

const tsToBigInt = (iso: string): bigint => BigInt(Date.parse(iso)) * 1_000n

const legacyStatusToEnum = (s: string): OrderStatus =>
  ({
    accepted: OrderStatus.Accepted,
    filled: OrderStatus.Filled,
    cancelled: OrderStatus.Canceled,
    rejected: OrderStatus.Rejected,
  })[s] ?? OrderStatus.Accepted

const legacySideToEnum = (s: string): Direction =>
  ({
    buy: Direction.Buy,
    sell: Direction.Sell,
  })[s] ?? Direction.Buy

const legacyOrderTypeToEnum = (s: string): OrderType =>
  ({
    market: OrderType.Market,
    limit: OrderType.Limit,
    stop: OrderType.Stop,
    "stop limit": OrderType.StopLimit,
  })[s] ?? OrderType.Market

const legacyTifToEnum = (s: string): TimeInForce =>
  ({
    day: TimeInForce.Day,
    gtc: TimeInForce.Gtc,
    opg: TimeInForce.Opg,
    cls: TimeInForce.Cls,
    ioc: TimeInForce.Ioc,
    fok: TimeInForce.Fok,
  })[s] ?? TimeInForce.Day

const toEquityOrder = (
  o: (typeof orderHistoryMockData)[number],
): EquityOrder => ({
  id: o.id,
  symbol: o.symbol,
  side: legacySideToEnum(o.side),
  notional: Number(o.notional) || 0,
  qty: Number(o.qty),
  orderType: legacyOrderTypeToEnum(o.orderType),
  timeInForce: o.time_in_force
    ? legacyTifToEnum(o.time_in_force)
    : TimeInForce.Day,
  status: legacyStatusToEnum(o.status),
  filledQty: Number(o.filledQty) || 0,
  createdAt: tsToBigInt(o.createdAt),
  updateAt: tsToBigInt(o.updatedAt),
})

const success = (
  updateType: UpdateType,
  order: EquityOrder,
): EquityOrderUpdateResult => ({
  type: SUCCESS_EQUITY_ORDER_UPDATE_RESULT,
  payload: { updateType, order },
})

export const equityOrderUpdateResults: EquityOrderUpdateResult[] = [
  success(UpdateType.StateOfTheWorld, toEquityOrder(orderHistoryMockData[0])),
  success(UpdateType.StateOfTheWorld, toEquityOrder(orderHistoryMockData[1])),
  success(UpdateType.StateOfTheWorld, toEquityOrder(orderHistoryMockData[2])),
  success(UpdateType.StateOfTheWorld, toEquityOrder(orderHistoryMockData[3])),
  success(UpdateType.Added, toEquityOrder(orderHistoryMockData[0])),
  success(UpdateType.Updated, {
    ...toEquityOrder(orderHistoryMockData[0]),
    status: OrderStatus.Filled,
    updateAt: tsToBigInt("2023-12-01T10:00:00Z"),
  }),
  success(UpdateType.Deleted, toEquityOrder(orderHistoryMockData[1])),
]
